// src/features/ucits-etf/components/AutoSuggest.tsx
//
// Premium "Spotlight"-style autocomplete for the UCITS-ETF universe. A
// self-contained island: it owns its own debounced query state and calls
// the in-memory Polars /suggest endpoint through TanStack Query (5-min
// cache), then hands the chosen symbol up to the parent via `onSelect`.
//
// Design notes:
//   - Absolute-positioned dropdown so it overlays rather than reflowing the
//     page beneath it.
//   - backdrop-blur + translucent surfaces for the macOS-spotlight feel.
//   - Full dark-mode parity with the rest of the Forge Ultra shell.
//   - tabular-nums is irrelevant here (no numeric columns), but keyboard
//     affordances (Escape to close, click-outside) keep it feeling native.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ExternalLink, Loader2, Search } from 'lucide-react';

import { useEtfSuggest } from '../api/etfQueries';
import { etfInfoPath } from '../routes';

interface AutoSuggestProps {
    onSelect: (symbol: string) => void;
}

export const AutoSuggest = ({ onSelect }: AutoSuggestProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Debounce typing by 200ms so the /suggest endpoint is queried once per
    // pause rather than on every keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTerm(searchTerm), 200);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const { data: suggestions, isFetching } = useEtfSuggest(debouncedTerm);

    // Close the dropdown when clicking outside the component.
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (symbol: string) => {
        onSelect(symbol);
        setSearchTerm('');
        setDebouncedTerm('');
        setIsOpen(false);
    };

    const hasResults = Boolean(suggestions && suggestions.length > 0);

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div className="relative flex items-center group">
                <Search
                    className="absolute left-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors"
                    size={18}
                />
                <input
                    type="text"
                    placeholder="Search UCITS ETFs by name, symbol or ISIN…"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setIsOpen(false);
                    }}
                    className="w-full pl-11 pr-10 py-3 rounded-2xl text-sm font-medium outline-none transition-all
                               bg-white/80 dark:bg-slate-900/70 backdrop-blur-md
                               border border-slate-300 dark:border-slate-700
                               text-slate-800 dark:text-slate-100
                               placeholder-slate-400 dark:placeholder-slate-500
                               shadow-sm focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                />
                {isFetching && (
                    <Loader2
                        className="absolute right-4 text-emerald-500 animate-spin"
                        size={18}
                    />
                )}
            </div>

            {isOpen && hasResults && (
                <ul
                    className="absolute z-50 w-full mt-2 max-h-80 overflow-y-auto rounded-2xl overflow-hidden animate-fade-in
                               bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl
                               border border-slate-200 dark:border-slate-700 shadow-2xl"
                >
                    {suggestions!.map((etf) => (
                        <li
                            key={etf.symbol}
                            onClick={() => handleSelect(etf.symbol)}
                            className="group flex items-center justify-between gap-3 px-5 py-3 cursor-pointer transition-colors
                                       border-b border-slate-100 dark:border-slate-800 last:border-0
                                       hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        >
                            <div className="flex flex-col min-w-0">
                                <span className="font-bold text-slate-900 dark:text-slate-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                                    {etf.symbol}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate max-w-[280px]">
                                    {etf.name}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {etf.domicile && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md
                                                     bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400
                                                     border border-slate-200 dark:border-slate-700">
                                        {etf.domicile}
                                    </span>
                                )}
                                {etf.is_ucits && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md
                                                     bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300
                                                     border border-emerald-200 dark:border-emerald-500/40">
                                        UCITS
                                    </span>
                                )}
                                <button
                                    type="button"
                                    aria-label={`Open ${etf.symbol} deep-dive`}
                                    title="Open fund deep-dive"
                                    onClick={(e) => {
                                        // Do not trigger the row's similarity
                                        // modal — this is a dedicated deep-link.
                                        e.stopPropagation();
                                        setIsOpen(false);
                                        navigate(etfInfoPath(etf.symbol));
                                    }}
                                    className="p-1.5 rounded-md transition-colors
                                               text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400
                                               hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                                >
                                    <ExternalLink size={14} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {isOpen &&
                !isFetching &&
                debouncedTerm.trim().length >= 2 &&
                !hasResults && (
                    <div
                        className="absolute z-50 w-full mt-2 rounded-2xl px-5 py-4 text-sm font-medium animate-fade-in
                                   bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl
                                   border border-slate-200 dark:border-slate-700 shadow-2xl
                                   text-slate-400 dark:text-slate-500"
                    >
                        No UCITS ETFs match “{debouncedTerm.trim()}”.
                    </div>
                )}
        </div>
    );
};

export default AutoSuggest;
